import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { ArrowLeft, ChevronDown, ChevronUp, Plus, Trash2, Users as UsersIcon } from "react-feather";
import {
  useGetCustomerQuery,
  useReplaceCustomerAssignmentsMutation,
  useSetAssignmentStatusMutation,
} from "../customersApi";
import { useCanEditGuardAssignments } from "../useGuardPermissions";
import { paths } from "../../../../routes/paths";

// Areas would come from area_master in the source app - kept as a simple
// fixed list here since no dedicated Areas master endpoint exists.
const AREA_OPTIONS = ["Main Gate", "Perimeter", "Warehouse", "Reception", "Parking"];
const DAY_NIGHT_OPTIONS = ["Day", "Night", "Both"];

const emptyPost = { assignmentName: "", assignmentArea: "", dayNight: "Day", locationId: "" };

// Dedicated screen (own route, reached by a button - never a tab or an
// expand panel) mirroring texas_master's "Guarding Assignments" tab from
// Frm_Customer, now against the live Milestone 3 + Guard Assignments
// follow-up endpoints (handoff §9.7). Adding/removing posts uses the
// full-list diff PUT .../assignments (assignmentId is never client-sent -
// always server-generated); activating/deactivating one post uses the
// dedicated POST .../assignments/{assignmentId}/status endpoint, which
// carries its own effective-date/permanent-vs-temp window - the modal this
// restores.
//
// Multi-location customers (handoff §19.6/§9.7 update, added 2026-08-05):
// every assignment now belongs to a specific site (locationId/siteLabel).
// For the common single-location customer this screen is visually
// unchanged - no location picker, no grouping, one flat table, same as
// before. Only once a customer has more than one location does a Location
// picker appear on the add-post form and the table group by site - saving
// then issues one location-scoped PUT per group instead of one flat PUT
// (verified live: a location-scoped PUT only ever touches that location's
// own rows, so this can't accidentally wipe another site's assignments).
const GuardingAssignmentsPage = () => {
  const { branchId, custId } = useParams();
  const navigate = useNavigate();
  const canEdit = useCanEditGuardAssignments();
  const { data: detail, isLoading, isError, error } = useGetCustomerQuery({ branchId, custId });
  const [replaceAssignments, { isLoading: isSaving }] = useReplaceCustomerAssignmentsMutation();
  const [setAssignmentStatus, { isLoading: isTogglingStatus }] = useSetAssignmentStatusMutation();

  const [assignments, setAssignments] = useState([]);
  const [post, setPost] = useState(emptyPost);
  const locations = detail?.locations || [];
  const hasMultipleLocations = locations.length > 1;
  const primaryLocationId = locations.find((l) => l.isPrimary)?.locationId ?? locations[0]?.locationId ?? null;
  const [deactivating, setDeactivating] = useState(null); // the assignment row being deactivated
  const [effectiveDate, setEffectiveDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [isTemp, setIsTemp] = useState(false);
  const [tempEndDate, setTempEndDate] = useState(dayjs().add(30, "day").format("YYYY-MM-DD"));
  // Inactive posts are historical - shown separately, collapsed by default,
  // and never editable from this screen (no Active checkbox, no remove).
  // New posts (handleAddPost) always start isActive: 1, so they land in the
  // Active section automatically with no extra wiring needed.
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (detail?.assignments) setAssignments(detail.assignments);
  }, [detail]);

  if (isError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Link to={paths.customers} className="d-inline-flex align-items-center mb-3">
            <ArrowLeft size={16} className="me-1" /> Back to Customers
          </Link>
          <div className="alert alert-danger">
            Could not load this customer ({error?.message || "server error"}).
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || !detail) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  const customer = detail.customer;

  const handleAddPost = () => {
    if (!post.assignmentName.trim() || !post.assignmentArea) {
      toast.error("Post name and area are required");
      return;
    }
    setAssignments((prev) => [
      ...prev,
      {
        recordGuid: null,
        assignmentName: post.assignmentName.trim(),
        assignmentArea: post.assignmentArea,
        dayNight: post.dayNight,
        isActive: 1,
        locationId: post.locationId || primaryLocationId,
      },
    ]);
    setPost(emptyPost);
  };

  const removeAssignment = (index) => {
    setAssignments((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSaveList = async () => {
    // Group by locationId and issue one scoped PUT per group - a
    // location-scoped PUT only ever replaces that location's own rows, so
    // this can be done as N independent calls without risking another
    // site's assignments (verified live, see the file-level comment above).
    const groups = new Map();
    assignments.forEach((a) => {
      const locId = a.locationId ?? primaryLocationId;
      if (!groups.has(locId)) groups.set(locId, []);
      groups.get(locId).push(a);
    });
    // A site that started with assignments but has had all of them removed
    // in this edit still needs its own PUT with an empty list, or the
    // server never learns those rows were deleted.
    locations.forEach((loc) => {
      if (!groups.has(loc.locationId)) groups.set(loc.locationId, []);
    });
    try {
      for (const [locationId, group] of groups) {
        // eslint-disable-next-line no-await-in-loop
        await replaceAssignments({
          branchId,
          custId,
          locationId,
          assignments: group.map((a) => ({
            recordGuid: a.recordGuid ?? null,
            assignmentName: a.assignmentName,
            assignmentArea: a.assignmentArea,
            dayNight: a.dayNight,
            isActive: Number(a.isActive) ? 1 : 0,
          })),
        }).unwrap();
      }
      toast.success("Guarding assignments saved");
    } catch (err) {
      toast.error(err?.message || "Could not save assignments");
    }
  };

  const openDeactivateModal = (assignment) => {
    setDeactivating(assignment);
    setEffectiveDate(dayjs().format("YYYY-MM-DD"));
    setIsTemp(false);
    setTempEndDate(dayjs().add(30, "day").format("YYYY-MM-DD"));
  };

  const handleToggleActive = async (assignment, checked) => {
    if (!checked) {
      openDeactivateModal(assignment);
      return;
    }
    // Reactivating doesn't need the date window in the source screen -
    // only deactivation opens the panel there.
    try {
      await setAssignmentStatus({
        branchId,
        custId,
        assignmentId: assignment.assignmentId,
        isActive: true,
        effectiveDate: dayjs().format("YYYY-MM-DDT00:00:00"),
        isTemp: false,
        tempEndDate: null,
      }).unwrap();
      toast.success("Assignment activated");
    } catch (err) {
      toast.error(err?.message || "Could not update assignment");
    }
  };

  const renderAssignmentRow = (a, index) => (
    <tr key={a.recordGuid || `${a.assignmentId}-${index}`}>
      <td>{a.assignmentName}</td>
      <td>{a.assignmentArea}</td>
      <td>{a.dayNight}</td>
      <td>
        <div className="form-check">
          <input
            type="checkbox"
            className="form-check-input"
            checked={Boolean(Number(a.isActive))}
            onChange={(e) => handleToggleActive(a, e.target.checked)}
            disabled={!canEdit || !a.assignmentId || isTogglingStatus}
          />
        </div>
      </td>
      <td>
        {a.effectiveDate
          ? `${dayjs(a.effectiveDate).format("DD-MMM-YYYY")}${
              a.isTemp && a.tempEndDate ? ` to ${dayjs(a.tempEndDate).format("DD-MMM-YYYY")}` : ""
            }`
          : "-"}
      </td>
      {canEdit && (
        <td>
          <button type="button" className="btn btn-link p-0 text-danger" onClick={() => removeAssignment(index)}>
            <Trash2 size={14} />
          </button>
        </td>
      )}
    </tr>
  );

  // Inactive rows are historical - no Active checkbox, no remove action,
  // read-only regardless of canEdit.
  const renderInactiveRow = (a, index) => (
    <tr key={a.recordGuid || `${a.assignmentId}-${index}`}>
      <td>{a.assignmentName}</td>
      <td>{a.assignmentArea}</td>
      <td>{a.dayNight}</td>
      <td>
        {a.effectiveDate
          ? `${dayjs(a.effectiveDate).format("DD-MMM-YYYY")}${
              a.isTemp && a.tempEndDate ? ` to ${dayjs(a.tempEndDate).format("DD-MMM-YYYY")}` : ""
            }`
          : "-"}
      </td>
    </tr>
  );

  const activeAssignments = assignments
    .map((a, index) => ({ a, index }))
    .filter(({ a }) => Boolean(Number(a.isActive)));
  const inactiveAssignments = assignments
    .map((a, index) => ({ a, index }))
    .filter(({ a }) => !Number(a.isActive));

  // Group by site only once a customer actually has more than one location -
  // for the common single-location case the table stays exactly as it was
  // before this feature, no grouping header. Shared by both the Active and
  // Inactive sections.
  const buildGroupedRows = (rows) =>
    hasMultipleLocations
      ? [...locations]
          .sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0))
          .map((loc) => ({
            location: loc,
            rows: rows.filter(({ a }) => (a.locationId ?? primaryLocationId) === loc.locationId),
          }))
          .filter((group) => group.rows.length > 0)
      : null;

  const activeGroupedRows = buildGroupedRows(activeAssignments);
  const inactiveGroupedRows = buildGroupedRows(inactiveAssignments);

  const confirmDeactivate = async () => {
    try {
      await setAssignmentStatus({
        branchId,
        custId,
        assignmentId: deactivating.assignmentId,
        isActive: false,
        effectiveDate: dayjs(effectiveDate).format("YYYY-MM-DDT00:00:00"),
        isTemp,
        tempEndDate: isTemp ? dayjs(tempEndDate).format("YYYY-MM-DDT00:00:00") : null,
      }).unwrap();
      toast.success("Assignment deactivated");
      setDeactivating(null);
    } catch (err) {
      toast.error(err?.message || "Could not deactivate assignment");
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header flex-wrap">
          <div className="add-item d-flex" style={{ minWidth: 0 }}>
            <div className="page-title" style={{ maxWidth: 700 }}>
              <Link
                to={paths.customerEdit(branchId, custId)}
                className="d-inline-flex align-items-center mb-2 text-truncate"
                style={{ maxWidth: "100%" }}
                title={customer.custName}
              >
                <ArrowLeft size={16} className="me-1 flex-shrink-0" /> Back to {customer.custName}
              </Link>
              <h4>Guarding Assignments</h4>
              <h6 style={{ wordBreak: "break-word" }}>{customer.custName}</h6>
            </div>
          </div>
          <div className="page-btn">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => navigate(paths.customerBulkGuards(branchId, custId))}
            >
              <UsersIcon size={16} className="me-2" />
              Bulk Entry
            </button>
          </div>
        </div>

        {!canEdit && (
          <div className="alert alert-info">
            You have read-only access. Editing assignments requires the Deployment.Edit permission.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            {canEdit && (
              <>
                <h5 className="mb-3">Add Guarding Post</h5>
                <div className="row">
                  {hasMultipleLocations && (
                    <div className="col-md-3 mb-3">
                      <label className="form-label">Location</label>
                      <select
                        className="form-control"
                        value={post.locationId || primaryLocationId || ""}
                        onChange={(e) => setPost((prev) => ({ ...prev, locationId: Number(e.target.value) }))}
                      >
                        {locations.map((loc) => (
                          <option key={loc.locationId} value={loc.locationId}>
                            {loc.siteLabel}{loc.isPrimary ? " (Primary)" : ""}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Assignment Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={post.assignmentName}
                      onChange={(e) => setPost((prev) => ({ ...prev, assignmentName: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Area</label>
                    <select
                      className="form-control"
                      value={post.assignmentArea}
                      onChange={(e) => setPost((prev) => ({ ...prev, assignmentArea: e.target.value }))}
                    >
                      <option value="">--Select--</option>
                      {AREA_OPTIONS.map((area) => (
                        <option key={area} value={area}>{area}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-3 mb-3">
                    <label className="form-label">Day / Night</label>
                    <select
                      className="form-control"
                      value={post.dayNight}
                      onChange={(e) => setPost((prev) => ({ ...prev, dayNight: e.target.value }))}
                    >
                      {DAY_NIGHT_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-1 mb-3 d-flex align-items-end">
                    <button type="button" className="btn btn-primary" onClick={handleAddPost}>
                      <Plus size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}

            <h5 className="mb-3">Active Assignments</h5>
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Assignment</th>
                    <th>Area</th>
                    <th>Day / Night</th>
                    <th>Active</th>
                    <th>Effective</th>
                    {canEdit && <th />}
                  </tr>
                </thead>
                <tbody>
                  {activeGroupedRows
                    ? activeGroupedRows.map((group) => (
                        <React.Fragment key={group.location.locationId}>
                          <tr className="table-light">
                            <td colSpan={canEdit ? 6 : 5} className="fw-semibold">
                              {group.location.siteLabel}
                              {group.location.isPrimary && (
                                <span className="badge badge-linesuccess ms-2">Primary</span>
                              )}
                            </td>
                          </tr>
                          {group.rows.map(({ a, index }) => renderAssignmentRow(a, index))}
                        </React.Fragment>
                      ))
                    : activeAssignments.map(({ a, index }) => renderAssignmentRow(a, index))}
                  {activeAssignments.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted">No active guarding posts.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {canEdit && (
              <button type="button" className="btn btn-primary" onClick={handleSaveList} disabled={isSaving}>
                Save Changes
              </button>
            )}
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <button
              type="button"
              className="btn btn-link p-0 d-flex align-items-center text-decoration-none"
              onClick={() => setShowInactive((prev) => !prev)}
            >
              {showInactive ? <ChevronUp size={16} className="me-2" /> : <ChevronDown size={16} className="me-2" />}
              <h5 className="mb-0">Inactive Assignments ({inactiveAssignments.length})</h5>
            </button>

            {showInactive && (
              <div className="table-responsive mt-3">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Assignment</th>
                      <th>Area</th>
                      <th>Day / Night</th>
                      <th>Effective</th>
                    </tr>
                  </thead>
                  <tbody>
                    {inactiveGroupedRows
                      ? inactiveGroupedRows.map((group) => (
                          <React.Fragment key={group.location.locationId}>
                            <tr className="table-light">
                              <td colSpan={4} className="fw-semibold">
                                {group.location.siteLabel}
                                {group.location.isPrimary && (
                                  <span className="badge badge-linesuccess ms-2">Primary</span>
                                )}
                              </td>
                            </tr>
                            {group.rows.map(({ a, index }) => renderInactiveRow(a, index))}
                          </React.Fragment>
                        ))
                      : inactiveAssignments.map(({ a, index }) => renderInactiveRow(a, index))}
                    {inactiveAssignments.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-muted">No inactive guarding posts.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal show={Boolean(deactivating)} onHide={() => setDeactivating(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Deactivate {deactivating?.assignmentName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Effective Date</label>
            <input type="date" className="form-control" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} />
          </div>
          <div className="d-flex gap-4 mb-3">
            <div className="form-check">
              <input type="radio" className="form-check-input" id="deact-permanent" checked={!isTemp} onChange={() => setIsTemp(false)} />
              <label className="form-check-label" htmlFor="deact-permanent">Permanent</label>
            </div>
            <div className="form-check">
              <input type="radio" className="form-check-input" id="deact-temp" checked={isTemp} onChange={() => setIsTemp(true)} />
              <label className="form-check-label" htmlFor="deact-temp">Temp</label>
            </div>
          </div>
          {isTemp && (
            <div className="mb-3">
              <label className="form-label">Temp End Date</label>
              <input type="date" className="form-control" value={tempEndDate} onChange={(e) => setTempEndDate(e.target.value)} />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeactivating(null)}>Cancel</Button>
          <Button variant="primary" onClick={confirmDeactivate} disabled={isTogglingStatus}>Ok</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GuardingAssignmentsPage;
